/**
 * React Starter Kit (https://www.reactstarterkit.com/)
 *
 * Copyright © 2014-present Kriasoft, LLC. All rights reserved.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE.txt file in the root directory of this source tree.
 */

import React from 'react';
import PropTypes from 'prop-types';
import { defineMessages, FormattedMessage } from 'react-intl';
import withStyles from 'isomorphic-style-loader/withStyles';
import { connect } from 'react-redux';
import cn from 'classnames';
import { getSessionUser } from '../../reducers';
import s from './Navigation.css';
import Link from '../Link';
import Menu from '../Menu';
import { canAccess } from '../../organization';

const messages = defineMessages({
  about: {
    id: 'about',
    defaultMessage: 'About',
    description: 'About label',
  },

  admin: {
    id: 'navigation.admin',
    defaultMessage: 'Admin',
    description: 'Admin link in header',
  },
  websites: {
    id: 'websites',
    defaultMessage: 'Websites',
    description: 'Website label',
  },
  webpages: {
    id: 'webpages',
    defaultMessage: 'Webpages',
    description: 'Webpages label',
  },
  privacy: {
    id: 'privacy',
    defaultMessage: 'Privacy',
    description: 'Privacy label',
  },

  profile: {
    id: 'profile',
    defaultMessage: 'Profile',
    description: 'Profile label',
  },
  users: {
    id: 'users',
    defaultMessage: 'Users',
    description: 'Users label',
  },
});

const contents = [{ id: 'websites', name: 'websites', path: '/websites/' }];

const makeLink = (linkData, currentPath) => {
  const label = [
    // eslint-disable-next-line react/jsx-props-no-spreading
    <FormattedMessage key={linkData.id} {...messages[linkData.name]} />,
  ];

  return (
    <Link //eslint-disable-line
      key={linkData.id}
      className={cn(s.link, currentPath === linkData.path ? s.current : null)}
      to={linkData.path}
    >
      {label}
    </Link>
  );
};

class Navigation extends React.Component {
  getMenu() {
    const { path, user } = this.props;

    const links = [];

    if (user && user.id) {
      links.push(contents.map((p) => makeLink(p, path)));
    }
    links.push(makeLink({ id: 'about', path: '/about', name: 'about' }, path));
    links.push(
      makeLink({ id: 'privacy', path: '/privacy', name: 'privacy' }, path),
    );

    if (canAccess(user, 'Admin')) {
      links.push(makeLink({ id: 7, path: '/admin', name: 'admin' }, path));
    }
    return links;
  }

  render() {
    const { small } = this.props;
    return (
      <span role="navigation" style={{ margin: '0 0.2em' }}>
        {!small && <div>{this.getMenu()}</div>}
        {small && (
          <div>
            <Menu
              plain
              passTrough
              items={this.getMenu()}
              dropAlign={{ top: 'top', right: 'right' }}
              icon={
                <svg
                  version="1.1"
                  viewBox="0 0 24 24"
                  width="24px"
                  height="24px"
                  role="img"
                  aria-label="menu"
                >
                  <path
                    fill="none"
                    stroke="#000"
                    strokeWidth="2"
                    d="M2,19 L22,19 M2,5 L22,5 M2,12 L22,12"
                  />
                </svg>
              }
            />
          </div>
        )}
      </span>
    );
  }
}

const mapToProps = (state) => ({
  path: state.ui.loading.path,
  user: getSessionUser(state),
});
Navigation.propTypes = {
  path: PropTypes.string.isRequired,
  user: PropTypes.shape({ id: PropTypes.number }),
  small: PropTypes.bool.isRequired,
};

Navigation.defaultProps = {
  user: null,
};

export default connect(mapToProps, null)(withStyles(s)(Navigation));
